import React, { useState } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../../config/pagination";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Backdrop,
  CircularProgress,
  Tooltip,
  Grid,
  TablePagination,
  Snackbar,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  Add,
  MoreVert,
  Delete,
  Lock,
  LockOpen,
  Visibility,
  Edit,
  Refresh,
  Analytics,
  Print,
} from "@mui/icons-material";
import {
  useTimetable,
  useTimetableFilterOptions,
  useDeleteTimetable,
  useLockTimetable,
  useUnlockTimetable,
} from "../../../hooks/queries/useTimetable";
import { queryKeys } from "../../../hooks/queries/queryKeys";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useEffectiveRolePath from "../../../hooks/useEffectiveRolePath";
import { format } from "date-fns";
import PermissionGate from "../../../components/common/PermissionGate";

const TimetableManagement = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { rolePath } = useEffectiveRolePath();

  // Format timetable name to make timestamps human readable
  const formatTimetableName = (name) => {
    if (!name) return name;
    
    // Check if name contains "Generated Timetable" with ISO timestamp
    const match = name.match(/^Generated Timetable (\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)$/);
    if (match) {
      try {
        const date = new Date(match[1]);
        const formattedDate = format(date, "MMM dd, yyyy 'at' h:mm a");
        return `Generated Timetable ${formattedDate}`;
      } catch (error) {
        // If parsing fails, return original name
        return name;
      }
    }
    
    return name;
  };

  const [filters, setFilters] = useState({
    academicYear: "",
    status: "", // draft | active | locked | archived
    departmentId: "", // "" = all, "none" = institute-level bucket, or a department id
  });
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 10,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTimetable, setSelectedTimetable] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [statusUpdatePendingId, setStatusUpdatePendingId] = useState(null);
  

  const { data: filterOptionsData } = useTimetableFilterOptions();
  const filterOptions = filterOptionsData?.data?.data ?? filterOptionsData?.data ?? {};
  const academicYearOptions = filterOptions.academicYears ?? [];
  const departmentOptions = filterOptions.departments ?? [];
  // Only show the department filter when the backend offers more than one option
  // (admins get all institute departments; HOD is auto-scoped so the filter is hidden).
  const showDepartmentFilter = departmentOptions.length > 1;
  const statusOptions = filterOptions.statuses ?? [
    { value: "draft", label: "Draft" },
    { value: "active", label: "Active" },
    { value: "locked", label: "Locked" },
    { value: "archived", label: "Archived" },
  ];

  const queryParams = {
    page: pagination.page + 1,
    limit: pagination.limit,
    ...(filters.academicYear && { academicYear: filters.academicYear }),
    ...(filters.status && { status: filters.status }),
    ...(filters.departmentId && { departmentId: filters.departmentId }),
  };

  const { data: timetablesData, isLoading, error } = useTimetable(queryParams);
  const deleteMutation = useDeleteTimetable();
  const lockMutation = useLockTimetable();
  const unlockMutation = useUnlockTimetable();

  const handleMenuOpen = (event, timetable) => {
    setAnchorEl(event.currentTarget);
    setSelectedTimetable(timetable);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleView = (id) => {
    navigate(`${rolePath}/timetable/view/${id}`);
    handleMenuClose();
  };

  const handlePdfView = (id) => {
    navigate(`${rolePath}/timetable/pdf/${id}`);
    handleMenuClose();
  };

  const handleEdit = (id) => {
    navigate(`${rolePath}/timetable/edit/${id}`);
    handleMenuClose();
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(selectedTimetable.id);
      setSnackbar({ open: true, message: "Timetable deleted successfully", severity: "success" });
      setDeleteDialogOpen(false);
      handleMenuClose();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || "Failed to delete timetable", severity: "error" });
    }
  };

  const patchTimetableListCache = (updatedRow, activating) => {
    if (!updatedRow?.id) return;
    const patchList = (list) =>
      Array.isArray(list)
        ? list.map((t) => {
            if (Number(t.id) === Number(updatedRow.id)) {
              return {
                ...t,
                ...updatedRow,
                status: updatedRow.status ?? (activating ? "active" : "draft"),
                isActive: updatedRow.isActive ?? activating,
              };
            }
            if (activating && getTimetableStatusKey(t) === "active") {
              return { ...t, status: "draft", isActive: false };
            }
            return t;
          })
        : list;

    queryClient.setQueriesData({ queryKey: queryKeys.timetable.all }, (old) => {
      if (!old) return old;
      const data = old?.data;
      if (!data) return old;
      if (data?.data?.timetables) {
        return {
          ...old,
          data: { ...data, data: { ...data.data, timetables: patchList(data.data.timetables) } },
        };
      }
      if (Array.isArray(data?.data)) {
        return { ...old, data: { ...data, data: patchList(data.data) } };
      }
      if (Array.isArray(data?.timetables)) {
        return { ...old, data: { ...data, timetables: patchList(data.timetables) } };
      }
      return old;
    });
  };

  const handleToggleLock = async (timetable) => {
    setStatusUpdatePendingId(timetable.id);
    handleMenuClose();
    try {
      const isLive = isTimetableLiveActive(timetable);
      const activating = !isLive;
      const res = isLive
        ? await unlockMutation.mutateAsync(timetable.id)
        : await lockMutation.mutateAsync(timetable.id);
      const updated = res?.data?.data;
      patchTimetableListCache(updated, activating);
      setSnackbar({
        open: true,
        message: isLive ? "Timetable moved to Draft" : "Timetable moved to Active",
        severity: "success",
      });
      void queryClient.refetchQueries({
        queryKey: [...queryKeys.timetable.all, queryParams],
        type: "active",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update timetable status",
        severity: "error",
      });
    } finally {
      setStatusUpdatePendingId(null);
    }
  };

  const handleAnalytics = (id) => {
    navigate(`${rolePath}/timetable/analytics/${id}`);
    handleMenuClose();
  };

  const handleRegenerate = (id) => {
    navigate(`${rolePath}/timetable/regenerate/${id}`);
    handleMenuClose();
  };

  // Handle different response structures
  const timetables =
    timetablesData?.data?.data?.timetables ||
    (Array.isArray(timetablesData?.data?.data) ? timetablesData.data.data : []);
  
  const paginationData = timetablesData?.data?.data?.pagination || timetablesData?.data?.pagination || timetablesData?.pagination;

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const isManualTimetable = (timetable) =>
    String(timetable?.generationMode || "").toLowerCase() === "fully_manual";

  /** GA fitness is an absolute score (~1000 baseline), not a 0–100 percentage. */
  const getFitnessQuality = (score) => {
    if (!Number.isFinite(score)) return null;
    if (score >= 900) return { label: "Excellent", color: "success" };
    if (score >= 700) return { label: "Good", color: "info" };
    if (score >= 500) return { label: "Acceptable", color: "warning" };
    if (score >= 300) return { label: "Poor", color: "warning" };
    return { label: "Critical", color: "error" };
  };

  /** Prefer API `status`; only fall back to `isActive` when status is missing (legacy). */
  const getTimetableStatusKey = (timetable) => {
    const st = String(timetable?.status ?? "").trim().toLowerCase();
    if (["draft", "active", "locked", "archived"].includes(st)) return st;
    return timetable?.isActive ? "active" : "draft";
  };

  const isTimetableLiveActive = (timetable) =>
    String(timetable?.status ?? "").trim().toLowerCase() === "active";

  const renderTimetableStatusChip = (timetable) => {
    if (statusUpdatePendingId && Number(timetable.id) === Number(statusUpdatePendingId)) {
      return (
        <Chip
          icon={<CircularProgress size={14} color="inherit" />}
          label="Updating..."
          size="small"
          color="default"
        />
      );
    }
    const key = getTimetableStatusKey(timetable);
    if (key === "locked") {
      return <Chip icon={<Lock />} label="Locked" size="small" color="default" />;
    }
    if (key === "archived") {
      return <Chip label="Archived" size="small" color="default" variant="outlined" />;
    }
    if (key === "active") {
      return <Chip label="Active" size="small" color="success" />;
    }
    return <Chip icon={<LockOpen />} label="Draft" size="small" color="warning" variant="outlined" />;
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Timetable Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and view all generated timetables
          </Typography>
        </Box>
        <PermissionGate permission="timetable.create">
          <Button
            variant="contained"
            startIcon={<Add />}
            disabled={Boolean(statusUpdatePendingId)}
            onClick={() => navigate(`${rolePath}/timetable/generate`)}
          >
            Generate New
          </Button>
        </PermissionGate>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Academic Year</InputLabel>
                <Select
                  value={filters.academicYear}
                  label="Academic Year"
                  onChange={(e) => handleFilterChange("academicYear", e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {academicYearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={showDepartmentFilter ? 3 : 4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {statusOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {showDepartmentFilter && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={filters.departmentId}
                    label="Department"
                    onChange={(e) => handleFilterChange("departmentId", e.target.value)}
                  >
                    <MenuItem value="">All departments</MenuItem>
                    {departmentOptions.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                    <MenuItem value="none">Institute-level (no department)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={showDepartmentFilter ? 2 : 4}>
              <Button
                fullWidth
                variant="outlined"
                sx={{ height: "100%" }}
                onClick={() => {
                  setFilters({ academicYear: "", status: "", departmentId: "" });
                  setPagination((prev) => ({ ...prev, page: 0 }));
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Timetables Table */}
      <Card>
        {isLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
            gap={2}
          >
            <CircularProgress size={48} />
            <Typography variant="body2" color="text.secondary">
              Loading timetable data...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load timetables: {error.message}
          </Alert>
        ) : timetables.length === 0 ? (
          <Box textAlign="center" sx={{ p: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Timetables Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate your first timetable to get started
            </Typography>
            <PermissionGate permission="timetable.create">
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate(`${rolePath}/timetable/generate`)}
              >
                Generate Timetable
              </Button>
            </PermissionGate>
          </Box>
        ) : (
          <Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timetable Name</TableCell>
                    {showDepartmentFilter && <TableCell>Department</TableCell>}
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Fitness Score</TableCell>
                    <TableCell>Slots/Classes/Teachers</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timetables.map((timetable) => (
                    <TableRow key={timetable.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {formatTimetableName(timetable.name || `Timetable ${timetable.id}`)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {timetable.academicYear || "N/A"}
                        </Typography>
                      </TableCell>
                      {showDepartmentFilter && (
                        <TableCell>
                          <Typography variant="body2">
                            {timetable.department?.name
                              || (timetable.departmentId == null ? "Institute-level" : `Dept ${timetable.departmentId}`)}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip 
                          label={timetable.timetableType || 'Automatic'} 
                          size="small" 
                          color="info" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell>{renderTimetableStatusChip(timetable)}</TableCell>
                      <TableCell>
                        {(() => {
                          if (isManualTimetable(timetable)) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                Not calculated
                              </Typography>
                            );
                          }
                          const score = Number(timetable.fitnessScore);
                          const quality = getFitnessQuality(score);
                          if (!quality) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                Not available
                              </Typography>
                            );
                          }
                          return (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {score.toFixed(0)}
                              </Typography>
                              <Chip
                                label={quality.label}
                                size="small"
                                color={quality.color}
                                sx={{ height: 20 }}
                              />
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {timetable.slotsCount || 0} slots
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {timetable.classesCount || 0} classes • {timetable.teachersCount || 0} teachers
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {timetable.createdAt
                            ? format(new Date(timetable.createdAt), "MMM dd, yyyy")
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            disabled={Boolean(statusUpdatePendingId)}
                            onClick={() => handleView(timetable.id)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print / PDF view">
                          <IconButton
                            size="small"
                            disabled={Boolean(statusUpdatePendingId)}
                            onClick={() => handlePdfView(timetable.id)}
                          >
                            <Print fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            disabled={Boolean(statusUpdatePendingId)}
                            onClick={() => handleEdit(timetable.id)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Analytics">
                          <IconButton
                            size="small"
                            disabled={Boolean(statusUpdatePendingId)}
                            onClick={() => handleAnalytics(timetable.id)}
                          >
                            <Analytics fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          disabled={Boolean(statusUpdatePendingId)}
                          onClick={(e) => handleMenuOpen(e, timetable)}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={paginationData?.total || 0}
              page={pagination.page}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={handleLimitChange}
              rowsPerPageOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              disabled={Boolean(statusUpdatePendingId)}
            />
          </Box>
        )}
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          disabled={!selectedTimetable?.id}
          onClick={() => {
            if (!selectedTimetable?.id) return;
            handleView(selectedTimetable.id);
          }}
        >
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem
          disabled={!selectedTimetable?.id}
          onClick={() => {
            if (!selectedTimetable?.id) return;
            handleEdit(selectedTimetable.id);
          }}
        >
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit Timetable
        </MenuItem>
        <MenuItem
          disabled={!selectedTimetable?.id}
          onClick={() => {
            if (!selectedTimetable?.id) return;
            handleRegenerate(selectedTimetable.id);
          }}
        >
          <Refresh fontSize="small" sx={{ mr: 1 }} />
          Regenerate
        </MenuItem>
        <MenuItem
          disabled={!selectedTimetable?.id}
          onClick={() => {
            if (!selectedTimetable?.id) return;
            handleAnalytics(selectedTimetable.id);
          }}
        >
          <Analytics fontSize="small" sx={{ mr: 1 }} />
          View Analytics
        </MenuItem>
        <MenuItem
          disabled={
            !selectedTimetable?.id ||
            lockMutation.isPending ||
            unlockMutation.isPending ||
            String(selectedTimetable?.status ?? "")
              .trim()
              .toLowerCase() === "locked"
          }
          onClick={() => {
            if (!selectedTimetable) return;
            handleToggleLock(selectedTimetable);
          }}
        >
          {(lockMutation.isPending || unlockMutation.isPending) ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              {"Updating..."}
            </>
          ) : isTimetableLiveActive(selectedTimetable) ? (
            <>
              <LockOpen fontSize="small" sx={{ mr: 1 }} />
              Mark as Draft
            </>
          ) : (
            <>
              <Lock fontSize="small" sx={{ mr: 1 }} />
              Mark as Active
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setDeleteDialogOpen(false);
        }}
      >
        <DialogTitle>Delete Timetable?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this timetable? This action cannot
            be undone.
          </Typography>
          {selectedTimetable && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Academic Year:</strong> {selectedTimetable.academicYear || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {selectedTimetable.status || "N/A"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fixed viewport loader — visible without scrolling */}
      <Backdrop
        open={Boolean(statusUpdatePendingId)}
        sx={{
          position: "fixed",
          zIndex: (theme) => theme.zIndex.modal + 2,
          bgcolor: "rgba(0, 0, 0, 0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={8}
          sx={{
            width: 280,
            maxWidth: "calc(100vw - 32px)",
            px: 3,
            py: 2.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "#fff",
            borderRadius: 2,
          }}
        >
          <CircularProgress size={32} />
          <Typography variant="body2" fontWeight={600} color="text.primary" textAlign="center">
            Updating timetable status...
          </Typography>
        </Paper>
      </Backdrop>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimetableManagement;
